import { inject, Injectable } from '@angular/core';

import { TagItemInfo, WorkflowVariableDefinitionInfo } from '@blaze-shared/api-contracts-domain';
import { Formula } from '@blaze-shared/formulas-data-access';
import { UrlVariable } from '@blaze-shared/projects-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  ColumnType,
  DatasetColumn,
  DEFAULT_ID_COL,
  DEFAULT_ID_COL_LABEL,
  getColumnKey,
  getTagKey,
  inferDataType,
  isArrayColumn,
  isArrayLikeColumn,
  isDatasetLinkValue,
  isNonObjectValueSchemaColumns,
  removeInternalNonIdColumns,
} from '@blaze-shared/schema-domain';
import {
  regexpGetLastObjectKey,
  regexpGetObjectKey,
  regexpGetStoredVariableName,
} from '@blaze-shared/shared-util';
import { normalizeVarName } from '@blaze-shared/variables-util';

import { getNamedInputColumnDisplay } from './intelli-autocomplete-display.utils';
import { IntelliValueFiltersService } from './intelli-value-filters.service';
import { ContextValue } from './models/intelli-autocomplete.models';
import { AutocompleteConfig, ParentFormula } from './models/intellisense';

const SEPARATION_TOKEN = ' | ';

export interface DisplayFragment {
  text: string;
  isBold: boolean;
}

@Injectable({ providedIn: 'root' })
export class IntelliAutocompleteService {
  private readonly iValueFiltersService = inject(IntelliValueFiltersService);
  private readonly schemaService = inject(SchemaService);

  /**
   * Creates possible autocomplete value options for the given config.
   * @param config
   * @param parentValue - Parent value of schemaColumns. If present, we ignore all other columns.
   * @returns
   */
  createValueOptions(
    config: AutocompleteConfig | null,
    parentValue?: ContextValue,
  ): ContextValue[] {
    const accountColumns = config?.accountColumns;
    const currentRowColumns = config?.currentRowColumns;
    const currentRowInnerColumns = config?.currentRowInnerColumns;
    const datasetColumns = config?.datasetColumns;
    const inputSchema = config?.inputSchema;
    const namedInputsColumns = config?.namedInputsColumns;
    const pageContext = config?.pageContext;
    const urlVars = config?.urlVars;
    const userColumns = config?.userColumns;
    const valuesFromColumn = config?.valuesFromColumn;
    const vars = config?.vars;
    let schemaColumns = config?.schemaColumns;
    let values: ContextValue[] = [];

    // If we're inside another value, we should only display either the index input or the keys of that value, and
    // ignore any other inputs.
    if (parentValue?.nestedColumns?.length) {
      if (this.isArrayWithObjectsData(parentValue)) {
        // for example "Tags" elements.
        parentValue = this.convertArrayWithObjectsToNestedDataFormat(parentValue);
      }
      schemaColumns = parentValue.nestedColumns;
      values = this.addInputColumnsToValues(values, schemaColumns ?? [], parentValue);
      values = this.addDisplayTextToValues(values, valuesFromColumn, inputSchema, parentValue);
      return values;
    }

    // Add values to the dropdown
    if (schemaColumns) {
      values = this.addInputColumnsToValues(values, schemaColumns, parentValue);
    }
    if (namedInputsColumns) {
      values = this.addNamedInputColumnsToValues(values, namedInputsColumns);
    }
    if (datasetColumns?.length) {
      values = this.addDatasetColumnsToValues(values, datasetColumns);
    }
    if (pageContext?.length) {
      values = this.addPageContextColumnsToValues(values, pageContext);
    }
    if (urlVars?.length) {
      values = this.addUrlVarsToValues(values, urlVars);
    }
    if (currentRowColumns?.length) {
      values = this.addCurrentRowColumnsToValues(values, currentRowColumns);
    }
    if (currentRowInnerColumns?.length) {
      values = this.addCurrentRowInnerColumnsToValues(values, currentRowInnerColumns);
    }
    values = this.addStoredVarsToValues(values, vars);
    values = this.addAccountColumnsToValues(values, accountColumns);
    values = this.addUserColumnsToValues(values, userColumns);

    // Format display text
    values = this.addDisplayTextToValues(values, valuesFromColumn, inputSchema);
    return values;
  }

  addAccountColumnsToValues(
    values: ContextValue[],
    columns?: DatasetColumn[] | null,
  ): ContextValue[] {
    if (!columns?.length) {
      return values;
    }

    for (const col of columns) {
      const value = `$$Account["${getColumnKey(col)}"]`;
      values.push({
        columnIdOrName: col.id ?? col.name,
        columnType: col.columnType,
        displayedDataType: this.getDataType(undefined, col.dataType, col.multipleValues),
        id: crypto.randomUUID(),
        isArray: col.multipleValues || col.columnType === 'Array',
        isRowId: !!col.isId,
        nestedColumns: col.dataTypeDef?.columns || [],
        type: 'Global Variable',
        value,
        valueDataType: this.iValueFiltersService.getColumnDataType(col),
      });
    }
    return values;
  }

  addConstantsToValues(
    values: ContextValue[],
    constants: string[] | number[] | boolean[],
  ): ContextValue[] {
    for (const constValue of constants) {
      values.push({
        columnType: 'Tag',
        display: `${constValue}`,
        displayedDataType: this.getDataType(constValue, 'string', false),
        id: crypto.randomUUID(),
        isRowId: false,
        type: 'Constant',
        value: `${constValue}`,
        valueDataType: 'String',
      });
    }
    return values;
  }

  addCurrentRowColumnsToValues(values: ContextValue[], columns: DatasetColumn[]): ContextValue[] {
    if (!columns?.length) {
      return values;
    }
    for (const col of columns) {
      if (col.isInternal && !col.isId) {
        // For internal columns, allow only ID columns to be displayed
        continue;
      }
      const value = `$$currentRow["${getColumnKey(col)}"]`;
      values.push({
        columnIdOrName: col.id ?? col.name,
        columnType: col.columnType,
        displayedDataType: this.getDataType(undefined, col.dataType, col.multipleValues),
        id: crypto.randomUUID(),
        isArray: col?.multipleValues || col.dataType === 'Array',
        isRowId: !!col.isId,
        nestedColumns: col.dataTypeDef?.columns || [],
        type: 'Current Row',
        value,
        valueDataType:
          col?.dataType === 'Link' ? col.link?.dataType || col?.dataType : col?.dataType,
      });
    }
    return values;
  }

  addCurrentRowInnerColumnsToValues(
    values: ContextValue[],
    columns: DatasetColumn[],
  ): ContextValue[] {
    if (!columns?.length) {
      return values;
    }
    for (const col of columns) {
      if (col.isInternal && !col.isId) {
        // For internal columns, allow only ID columns to be displayed
        continue;
      }
      const value = `$$input1["${getColumnKey(col)}"]`;
      values.push({
        columnIdOrName: col.id ?? col.name,
        columnType: col.columnType,
        displayedDataType: this.getDataType(undefined, col.dataType, col.multipleValues),
        id: crypto.randomUUID(),
        isArray: col?.multipleValues || col.dataType === 'Array',
        isRowId: !!col.isId,
        nestedColumns: col.dataTypeDef?.columns || [],
        type: col.isId ? 'Row Identifier' : 'Data Field',
        value,
        valueDataType:
          col?.dataType === 'Link' ? col.link?.dataType || col?.dataType : col?.dataType,
      });
    }
    return values;
  }

  /**
   * Converts value into a display text.
   */
  addDisplayTextToValues(
    ctxValues: ContextValue[],
    valuesFromColumn?: any[],
    schema?: BlazeSchema | null,
    parentValue?: ContextValue,
  ): ContextValue[] {
    const updatedCtxValues: ContextValue[] = [];
    for (const ctxValue of ctxValues) {
      if (ctxValue && !ctxValue?.display) {
        const display = this.createDisplayText(ctxValue, valuesFromColumn, schema, parentValue);
        const autoCompleteDisplay = this.createAutocompleteText(display, parentValue);
        updatedCtxValues.push({
          ...ctxValue,
          autoCompleteDisplay,
          display,
          isAutoCompleted: this.shouldDisplayAsStylizedToken(ctxValue),
        });
      } else if (ctxValue) {
        const autoCompleteDisplay = this.createAutocompleteText(
          ctxValue.display || '',
          parentValue,
        );
        updatedCtxValues.push({
          ...ctxValue,
          autoCompleteDisplay,
          isAutoCompleted: this.shouldDisplayAsStylizedToken(ctxValue),
        });
      }
    }
    return updatedCtxValues;
  }

  addDatasetColumnsToValues(values: ContextValue[], columns: DatasetColumn[]): ContextValue[] {
    for (const col of columns) {
      values.push({
        columnIdOrName: col.id ?? col.name,
        columnType: col.columnType,
        display: `${getColumnKey(col)}`,
        displayedDataType: this.getDataType(undefined, col.dataType, col.multipleValues),
        id: crypto.randomUUID(),
        isArray: col.multipleValues || col.columnType === 'Array',
        isRowId: !!col.isId,
        type: col.isId ? 'Row Identifier' : 'Data Field',
        value: `{${getColumnKey(col)}}`,
        valueDataType: this.iValueFiltersService.getColumnDataType(col),
      });
    }
    return values;
  }

  addFormulasToValues(values: ContextValue[], formulas: Formula[]): ContextValue[] {
    for (const formula of formulas) {
      values.push({
        autoCompleteDisplay: formula.usage,
        columnType: 'Formula',
        description: formula.description || '',
        display: formula.name,
        displayedDataType: this.getDataType(undefined, formula.returnDataType, false),
        formula,
        id: crypto.randomUUID(),
        isAutoCompleted: true,
        isRowId: false,
        type: 'Formula',
        value: formula.name,
        valueDataType: formula.returnDataType,
      });
    }
    return values;
  }

  addInputColumnsToValues(
    values: ContextValue[],
    columns: DatasetColumn[],
    parentValue?: ContextValue,
  ): ContextValue[] {
    if (!columns?.length) {
      return values;
    }
    const isParentAnArray = this.isArrayData(parentValue);
    const valuePrefix = parentValue?.value || '$$input1';
    for (const col of columns) {
      if (!col.isInternal || col.isId) {
        // allow ID columns to be displayed
        let value = '';
        let valueDataType = col?.dataType;
        if (col.multipleValues) {
          valueDataType = 'Array';
        } else if (col?.dataType === 'Link') {
          valueDataType = col.link?.dataType;
        }

        if (isParentAnArray) {
          value = `${valuePrefix}[0]`;
        } else {
          value = `${valuePrefix}["${getColumnKey(col)}"]`;
        }

        values.push({
          columnIdOrName: col.id ?? col.name,
          columnType: col.columnType,
          displayedDataType: this.getDataType(undefined, col.dataType, col.multipleValues),
          id: crypto.randomUUID(),
          isArray: isArrayColumn(col),
          isRowId: !!col.isId,
          nestedColumns: col.dataTypeDef?.columns || [],
          type: col.isId ? 'Row Identifier' : 'Data Field',
          value,
          valueDataType,
        });
      }
    }
    return values;
  }

  addNamedInputColumnsToValues(
    values: ContextValue[],
    namedInputsColumns: Record<string, DatasetColumn[]>,
  ): ContextValue[] {
    for (const inputName in namedInputsColumns) {
      const columns = removeInternalNonIdColumns(namedInputsColumns[inputName] || []);

      for (const col of columns) {
        let value = `$$${inputName}["${getColumnKey(col)}"]`;
        // for non-object value columns the foreach above will execute once
        if (isNonObjectValueSchemaColumns(columns)) {
          value = `$$${inputName}`;
        }

        const display = getNamedInputColumnDisplay(col);
        values.push({
          display,
          columnIdOrName: col.id ?? col.name,
          columnType: col.columnType,
          displayedDataType: this.getDataType(undefined, col.dataType, col.multipleValues),
          id: crypto.randomUUID(),
          inputName,
          isArray: col?.multipleValues || col.dataType === 'Array',
          isRowId: !!col.isId,
          nestedColumns: col.dataTypeDef?.columns || [],
          type: col.isId ? 'Row Identifier' : 'Data Field',
          value,
          valueDataType: col?.dataType,
        });
      }
    }

    return values;
  }

  addPageContextColumnsToValues(values: ContextValue[], columns: DatasetColumn[]): ContextValue[] {
    for (const col of columns) {
      const value = `$$Page["${col.id ?? col.name}"]`;
      values.push({
        columnIdOrName: col.id ?? col.name,
        columnType: col.columnType,
        display: `Current Page | ${col.name}`,
        displayedDataType: this.getDataType(undefined, col.dataType, col.multipleValues),
        id: crypto.randomUUID(),
        isArray: isArrayColumn(col),
        isRowId: !!col.isId,
        nestedColumns: col.dataTypeDef?.columns || [],
        type: 'Current Page',
        value,
        valueDataType: this.iValueFiltersService.getColumnDataType(col),
      });
    }
    return values;
  }

  addStoredVarsToValues(
    values: ContextValue[],
    varDefs: Partial<WorkflowVariableDefinitionInfo>[] = [],
  ): ContextValue[] {
    for (const varDef of varDefs) {
      const varName = varDef.name;
      const valueDataType = varDef.dataType;
      const displayedDataType = this.getDataType(undefined, valueDataType, false);
      const value: ContextValue = {
        display: regexpGetStoredVariableName(varName),
        displayedDataType,
        id: crypto.randomUUID(),
        isRowId: false,
        type: 'Stored Variable',
        value: varName ? normalizeVarName(varName) : '',
        // Only saved values will have id and proper value data type
        valueDataType,
      };
      values.push(value);
    }
    return values;
  }

  addUrlVarsToValues(values: ContextValue[], urlVars: UrlVariable[]): ContextValue[] {
    for (const urlVar of urlVars) {
      const value: ContextValue = {
        display: `${urlVar.name}`,
        displayedDataType: inferDataType(urlVar.value),
        id: crypto.randomUUID(),
        isRowId: false,
        type: 'URL Parameter',
        value: `$$Url["${urlVar.name}"]`,
        valueDataType: inferDataType(urlVar.value),
      };
      values.push(value);
    }
    return values;
  }

  addTagValuesToValues(values: ContextValue[], tagValues: TagItemInfo[]): ContextValue[] {
    for (const tag of tagValues) {
      values.push({
        columnType: 'Tag',
        display: `${getTagKey(tag)}`,
        displayedDataType: this.getDataType(tag.value, 'Tag', false),
        id: crypto.randomUUID(),
        isRowId: false,
        type: 'Constant',
        value: `"${getTagKey(tag)}"`,
        valueDataType: 'Tag',
      });
    }
    return values;
  }

  addUserColumnsToValues(values: ContextValue[], columns?: DatasetColumn[] | null): ContextValue[] {
    if (!columns?.length) {
      return values;
    }
    for (const col of columns) {
      const value = `$$User["${getColumnKey(col)}"]`;
      values.push({
        columnIdOrName: col.id ?? col.name,
        columnType: col.columnType,
        displayedDataType: this.getDataType(undefined, col.dataType, col.multipleValues),
        id: crypto.randomUUID(),
        isArray: isArrayLikeColumn(col),
        isRowId: !!col.isId,
        nestedColumns: col.dataTypeDef?.columns || [],
        type: 'Global Variable',
        value,
        valueDataType: this.iValueFiltersService.getColumnDataType(col),
      });
    }
    return values;
  }

  addValuesFromColumnToValues(
    values: ContextValue[],
    col: DatasetColumn,
    valuesFromColumn: any[],
  ): ContextValue[] {
    for (const value of valuesFromColumn) {
      const isLink = isDatasetLinkValue(value);
      values.push({
        columnIdOrName: col.id ?? col.name,
        columnType: col.columnType,
        display: isLink ? `${value?.['display'] || ''}` : `${value}`,
        displayedDataType: this.getDataType(value, col.dataType, col.multipleValues),
        id: crypto.randomUUID(),
        isArray: isArrayColumn(col),
        isRowId: !!col.isId,
        type: 'Constant',
        value: isLink ? value?.['rowId'] || '' : value,
        valueDataType: this.iValueFiltersService.getColumnDataType(col),
      });
    }
    return values;
  }

  /**
   * Divides displayed text of a given value into substrings in order to be able to properly mark the text that matches
   * passed filterText.
   * @param value
   * @param filterText
   * @returns
   */
  createValueOptionDisplayFragments(value: ContextValue, filterText?: string): DisplayFragment[] {
    const valueText = this.getVisibleTextFromValue(value);
    filterText = filterText ? filterText || '' : undefined;

    const textFragments: { text: string; isBold: boolean }[] = [];
    let filterTextIndex = filterText
      ? valueText.toLowerCase().indexOf(filterText.toLowerCase())
      : -1;

    // Filter text not found at all - create a single fragment
    if (!filterText || (filterTextIndex < 0 && !textFragments.length)) {
      textFragments.push({ text: valueText, isBold: false });
      return textFragments;
    }

    let remainingText = valueText;
    while (remainingText?.length) {
      filterTextIndex = remainingText.toLowerCase().indexOf(filterText.toLowerCase());
      if (filterTextIndex < 0) {
        textFragments.push({ text: remainingText, isBold: false });
        remainingText = '';
      } else if (filterTextIndex === 0) {
        const textEnd = filterTextIndex + filterText.length;
        textFragments.push({
          text: remainingText.substring(0, textEnd),
          isBold: true,
        });
        remainingText = remainingText.substring(textEnd, remainingText.length);
      } else if (filterTextIndex > 0) {
        const textEnd = filterTextIndex + filterText.length;
        textFragments.push({
          text: remainingText.substring(0, filterTextIndex),
          isBold: false,
        });
        textFragments.push({
          text: remainingText.substring(filterTextIndex, textEnd),
          isBold: true,
        });
        remainingText = remainingText.substring(textEnd, remainingText.length);
      }
    }

    return textFragments;
  }

  filterFormulas(
    config: AutocompleteConfig | null,
    filterValue: string,
    parentFormula?: ParentFormula,
  ): Formula[] {
    const formulas = config?.formulas || [];
    const dataTypes = parentFormula?.parameter?.dataTypes;
    return formulas
      .filter((formula) => this.filterFormulaByDataTypes(formula, dataTypes))
      .filter((formula) => this.filterFormulaByEnteredText(formula, filterValue));
  }

  filterValues(
    config: AutocompleteConfig | null,
    values: ContextValue[],
    filterValue: string,
    parentFormula?: ParentFormula,
  ): ContextValue[] {
    values = values.filter((value) =>
      this.iValueFiltersService.filterOutUnsupportedDataType(value, config, parentFormula),
    );
    values = values.filter((value) =>
      this.iValueFiltersService.filterOutValueByEnteredText(value, filterValue),
    );
    values = this.iValueFiltersService.filterOutDuplicateValues(values);
    return values;
  }

  /**
   * Return datatype of the given value if known.
   */

  getDataType(value: any, dataType: any | undefined, multipleValues: boolean | undefined): string {
    dataType = dataType || typeof value;
    dataType = dataType === 'undefined' ? '' : dataType?.toLowerCase() || '';

    if (multipleValues) {
      return 'array';
    }

    switch (dataType) {
      case 'boolean':
        return 'check';
      case 'string':
        return 'text';
      default:
        return dataType;
    }
  }

  getVisibleTextFromValue(value?: ContextValue): string {
    const text =
      value?.formula?.name || value?.autoCompleteDisplay || value?.display || (value?.value ?? '');
    return this.schemaService.replaceColumnAndTagIds(text);
  }

  /**
   * Removes parent value prefix so when user accesses nested value, autocomplete shows only the latest key
   * and not the whole path.
   */
  private createAutocompleteText(displayValue: string, parentValue?: ContextValue): string {
    if (!parentValue) {
      return displayValue;
    }

    if (this.isArrayData(parentValue)) {
      return displayValue;
    }

    displayValue = displayValue.replaceAll(parentValue.display || '', '');
    if (displayValue.startsWith(SEPARATION_TOKEN)) {
      displayValue = displayValue.substring(SEPARATION_TOKEN.length, displayValue.length);
    }
    return displayValue;
  }

  private createDisplayText(
    ctxValue: ContextValue,
    valuesFromColumn?: any[],
    schema?: BlazeSchema | null,
    parentValue?: ContextValue,
  ): string {
    const { value, inputName } = ctxValue ?? {};
    const valueWithoutColumnIds = this.schemaService.replaceColumnIdsWithNames(value);

    let displayText = '';
    if (!value) {
      return '';
    }
    // $$Account
    else if (
      valueWithoutColumnIds === `$$Account["_id"]` ||
      valueWithoutColumnIds === '$$Account._id'
    ) {
      return 'Current Account';
    } else if (`${value}`.startsWith('$$Account')) {
      const colName = this.schemaService.getColumnName(regexpGetObjectKey(value));
      if (colName) {
        const colKey = this.schemaService.replaceColumnNamesWithIds(schema?.id || '', colName);
        displayText = `Current Account | ${this.appendComplexIdOrName(colKey ?? colName, ctxValue, parentValue)}`;
        return displayText;
      }
      return value;
    }

    // $$Page
    else if (`${value}`.startsWith('$$Page')) {
      const colName = this.schemaService.getColumnName(regexpGetObjectKey(value));
      if (colName) {
        const colKey = this.schemaService.replaceColumnNamesWithIds(schema?.id || '', colName);
        displayText = `Page | ${this.appendComplexIdOrName(colKey ?? colName, ctxValue, parentValue)}`;
        return displayText;
      }
      return value;
    }

    // $$User
    else if (valueWithoutColumnIds === `$$User["_id"]` || valueWithoutColumnIds === '$$User._id') {
      return 'Current User';
    } else if (`${value}`.startsWith('$$User')) {
      const colName = this.schemaService.getColumnName(regexpGetObjectKey(value));
      if (colName) {
        const colKey = this.schemaService.replaceColumnNamesWithIds(schema?.id || '', colName);
        displayText = `Current User | ${this.appendComplexIdOrName(colKey ?? colName, ctxValue, parentValue)}`;
        return displayText;
      }
      return value;
    }

    // $$input1 or $$row (currently selected table row) values.
    else if (value.startsWith('$$input1') || value.startsWith('$$row')) {
      const colName = regexpGetLastObjectKey(value);
      const colKey = this.schemaService.replaceColumnNamesWithIds(schema?.id || '', colName || '');
      displayText = this.appendComplexIdOrName(
        `${colKey ?? colName ?? value}`,
        ctxValue,
        parentValue,
      );
      return displayText;
    } else if (value.startsWith('$$output')) {
      const colName = regexpGetObjectKey(value);
      const colKey =
        ctxValue.columnIdOrName ??
        this.schemaService.replaceColumnNamesWithIds(schema?.id || '', colName || '');
      displayText = this.appendComplexIdOrName(colKey ?? colName ?? value, ctxValue, parentValue);
      return displayText;
    }

    // Other $$ values
    else if (value.startsWith('$$')) {
      const colName = regexpGetObjectKey(value);
      const colKey =
        ctxValue.columnIdOrName ??
        this.schemaService.replaceColumnNamesWithIds(schema?.id || '', colName || '');
      const displayValue = this.appendComplexIdOrName(
        colKey ?? colName ?? value,
        ctxValue,
        parentValue,
      );
      const separator = displayValue ? SEPARATION_TOKEN : '';
      displayText = inputName ? `${inputName}${separator}${displayValue}` : displayValue;
      return displayText;
    }

    // Boolean
    else if (value === 'true' || value === 'false') {
      return value;
    }

    // Linked data.
    if (valuesFromColumn?.length) {
      const linkedValue = valuesFromColumn.find((colValue) => {
        return isDatasetLinkValue(colValue) && colValue['rowId'] === value;
      });
      if (linkedValue) {
        return linkedValue['display'] || linkedValue['rowId'] || value;
      }
    }

    // Default
    return value;
  }

  private appendComplexIdOrName(
    displayText: string,
    ctxValue: ContextValue,
    parentValue?: ContextValue | undefined,
  ): string {
    if (
      ctxValue.isRowId &&
      this.schemaService.replaceColumnIdsWithNames(ctxValue.columnIdOrName) === DEFAULT_ID_COL
    ) {
      displayText = DEFAULT_ID_COL_LABEL;
    }
    if (!parentValue) {
      return displayText;
    }
    if (this.isArrayData(parentValue)) {
      return `${parentValue.display}[0]`;
    }
    return `${parentValue.display} | ${displayText}`;
  }

  private convertArrayWithObjectsToNestedDataFormat(contextValue: ContextValue): ContextValue {
    const columns = [...(contextValue.nestedColumns || [])];
    const nestedContextValue: ContextValue = {
      ...contextValue,
      nestedColumns: [
        {
          name: '',
          dataType: 'Object',
          columnType: 'Object',
          hasTimeZone: false,
          isProfilePicture: false,
          dataTypeDef: {
            ref: null,
            name: null,
            columns,
          },
        },
      ],
    };
    return nestedContextValue;
  }

  private filterFormulaByDataTypes(
    formula: Formula,
    dataTypes?: (ColumnType | undefined)[],
  ): boolean {
    dataTypes = dataTypes?.filter((dataType) => !!dataType) || [];
    const hasParamDataType = !!dataTypes?.length;
    if (!hasParamDataType) {
      // No formula parameter datatypes found - do not filter
      return true;
    }
    if (formula.returnDataType && hasParamDataType) {
      return dataTypes.includes(formula.returnDataType);
    }
    return true;
  }

  private filterFormulaByEnteredText(formula: Formula, text: string): boolean {
    text = `${text}`?.toLowerCase();
    return `${formula?.name}`?.toLowerCase().includes(text);
  }

  private isArrayData(contextValue?: ContextValue): boolean {
    if (!contextValue) {
      return false;
    }
    return (
      contextValue.columnType === 'Array' ||
      contextValue.valueDataType === 'Array' ||
      contextValue.isArray === true
    );
  }

  private isArrayWithObjectsData(contextValue: ContextValue): boolean {
    return (
      contextValue.columnType === 'Object' &&
      contextValue.isArray === true &&
      !!contextValue.nestedColumns
    );
  }

  private shouldDisplayAsStylizedToken(ctxValue: ContextValue): boolean {
    if (ctxValue.type === 'Formula') {
      return false;
    }
    return true;
  }
}
